import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { Sketch } from 'vue-color';
import { guid } from '@/mixins/guid';
import tinycolor from 'tinycolor2';
import Cropper from 'cropperjs';
import { cssGenerator } from '@/mixins/css-generator';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import path from 'path';
import { siteBuilder } from '@/mixins/site-builder';

export default Vue.component('theme', {
  template: require('./template.html'),
  name: 'theme',
  mixins: [guid, cssGenerator, siteBuilder, uploader],
  components: {
    'sketch-picker': Sketch,
    uploadProgress,
  },
  computed: {
    ...mapGetters(['currentSite', 'currentPage']),
    schemeColors() {
      return [{ color: '', type: 'picker' }];
    },
    selectionStyles() {
      const selector = _.find(this.currentSite.mediaQueries, {
        selector: '::selection',
      });
      const property = selector ? _.find(selector.properties, { property: 'background' }) : false;
      return property;
    },
  },
  data() {
    return {
      s3Url: process.env.S3_ASSETS_URL,
      hoveredFav: false,
      loaders: {
        favicon: false,
        colorPalette: false,
        selectionText: false,
      },
      favicon: null,
      edit: {
        favicon: false,
        colorPalette: false,
        selectionText: false,
      },
      changed: {
        favicon: false,
        colorPalette: false,
        selectionText: false,
      },
      colorPickers: {
        selection: false,
      },
      pickers: {},
      initial: {
        colorPalette: false,
        favicon: null,
        pageCSS: null,
        selectionText: null,
        mediaQueries: [],
      },
      localFile: null,
      openCropper: false,
      cropper: null,
    };
  },
  created() {
    this.checkNewValues();
  },
  mounted() {
    this.callCssGenerator();
  },
  watch: {
    'currentSite.favicon': function c(img) {
      this.changed.favicon = this.initial.favicon !== img;
    },
    updateInitial(from) {
      if (from === 'defaultFont') {
        this.initial.pageCSS = _.cloneDeep(this.currentSite.pageCSS);
        this.initial.mediaQueries = _.cloneDeep(this.currentSite.mediaQueries);
      }
    },
    openCropper(open) {
      if (open) {
        setTimeout(() => {
          const image = this.$refs.imageCropper;
          this.cropper = new Cropper(image, {
            aspectRatio: 1 / 1,
            dragMode: 'move',
            autoCropArea: 1,
            zoomable: false,
          });
        }, 300);
      }
    },
    showModal(o) {
      if (o) {
        this.setInitials();
      } else {
        this.onCloseModal();
      }
    },
  },
  props: ['show-modal', 'toggle-modal', 'update-initial'],
  methods: {
    setInitials() {
      this.initial.favicon = _.cloneDeep(this.currentSite.favicon);
      this.initial.pageCSS = _.cloneDeep(this.currentSite.pageCSS);
      this.initial.selectionText = _.cloneDeep(this.currentSite.selectionText);
      this.initial.mediaQueries = _.cloneDeep(this.currentSite.mediaQueries);
      this.initial.colorPalette = _.cloneDeep(this.currentSite.colorPalette);
      this.setColorPickers();
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    destroyCropper() {
      this.openCropper = false;
      this.localFile = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    async uploadCropped() {
      try {
        const canvas = this.cropper.getCroppedCanvas({
          width: 48,
          height: 48,
          imageSmoothingQuality: 'high',
        });
        const blob = await this.getBlob(canvas);
        // send off to our s3 upload mixin
        const filePath = `/_favicons/${this.localFile}`;
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: blob,
        });
        this.$store.dispatch('setFavicon', { favicon: filePath });
        this.destroyCropper();
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'favicon uploadCropped',
          });
        }
      } finally {
        if (this.showFiles) {
          setTimeout(() => {
            this.fetchImageFiles();
          }, 500);
        }
      }
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // gather naming information
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `drz_favicon_${_.trim(fileName)}`;
              fileName = _.replace(fileName, /\s+/g, '_');
              this.localFile = fileName;
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'favicon openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    callCssGenerator() {
      return this.generateCSS({
        drp: this.currentSite,
        hideTags: true,
      });
    },
    checkNewValues() {
      const values = [];
      if (!this.currentSite.colorPalette) {
        values.push(
          {
            key: 'colorPalette',
            value: [],
          },
        );
      }
      if (!this.currentSite.mediaQueries) {
        values.push({
          key: 'mediaQueries',
          value: [],
        });
      }
      if (!this.currentSite.globalCSS) {
        values.push({
          key: 'globalCSS',
          value: true,
        });
      }
      if (!this.currentSite.pageCSS) {
        values.push({
          key: 'pageCSS',
          value: true,
        });
      }
      if (!this.currentSite.selectionText) {
        const value = this._buildPickerVal('rgba(65, 224, 196, 1)');
        values.push({
          key: 'selectionText',
          value,
        });
      }
      if (values.length > 0) {
        this.$store.dispatch('setNewSiteValues', {
          site: this.currentSite,
          vm: this,
          values,
        });
      }
      if (!this.selectionStyles) {
        this.$store.dispatch('changeDropletCss', {
          drp: this.currentSite,
          val: 'default',
          viewport: 'desktop',
          property: 'background',
          selector: '::selection',
          noId: true,
        });
      }
    },
    addColor() {
      this.changed.colorPalette = true;
      const site = this.currentSite;
      const value = this._defaultColorPickerObj;
      const _id = this.guid();
      this.$store.dispatch('runStateFn', {
        fn() {
          site.colorPalette.push({
            _id,
            value,
          });
        },
      });
      this.$set(this.pickers, _id, false);
    },
    deleteColor(color) {
      this.changed.colorPalette = true;
      this.pickers[color._id] = false;
      const paletteColors = this.currentSite.colorPalette;
      const i = paletteColors.findIndex(c => c._id === color._id);
      this.$store.dispatch('runStateFn', {
        fn() {
          paletteColors.splice(i, 1);
        },
      });
      this.setColorPickers();
    },
    setColorPickers() {
      this.pickers = {};
      _.forEach(this.currentSite.colorPalette, (color) => {
        this.$set(this.pickers, color._id, false);
      });
    },
    openUpload(type) {
      this.$refs[type].click();
    },
    clearFavicon() {
      this.$store.dispatch('setFavicon', { favicon: false });
    },
    colorPickerChange(val, selector, property) {
      this.changed.selectionText = true;
      this._debounce(() => {
        const value = _.cloneDeep(val);
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key: 'selectionText',
          value,
        });
        const payload = {};
        payload.site = this.currentSite;
        payload.val = this.concatPicker(value);
        payload.property = property;
        payload.selector = selector;
        this.$store.dispatch('changeGlobalPageCss', payload);
        this.callCssGenerator();
      }, 200);
    },
    concatPicker(val) {
      return `rgba(${val.rgba.r}, ${val.rgba.g}, ${val.rgba.b}, ${val.rgba.a})`;
    },
    shadeHelper(val) {
      const color = tinycolor(val);
      let shade;
      if (color.isDark()) {
        shade = color.lighten(30).toString();
      } else {
        shade = color.darken(30).toString();
      }
      return shade;
    },
    toggleColorPicker(type) {
      _.forOwn(this.colorPickers, (value, key) => {
        if (type && key !== type) {
          this.colorPickers[key] = false;
        } else if (!type) {
          this.colorPickers[key] = false;
        }
      });
      if (type) {
        this.colorPickers[type] = !this.colorPickers[type];
      }
    },
    toggleColorThemePicker(_id) {
      _.forOwn(this.pickers, (value, picker) => {
        if (_id && picker !== _id) {
          this.pickers[picker] = false;
        } else if (!_id) {
          this.pickers[picker] = false;
        }
      });
      if (_id) {
        this.pickers[_id] = !this.pickers[_id];
      }
    },
    updatePaletteColor(value, color) {
      this.changed.colorPalette = true;
      this._debounce(() => {
        this.$store.dispatch('setGlobalValue', {
          item: color,
          key: 'value',
          value,
        });
      }, 200);
    },
    async uploadFavicon(e) {
      this.favicon = [...e.target.files];
      this.$validator.validate('favicon', this.favicon).then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          let newFile = [...e.target.files];
          const payload = {};
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `drz_favicon_${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');
            try {
              // send off to our s3 upload mixin
              const filePath = `/_favicons/${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
              });
              payload.favicon = filePath;
              this.$store.dispatch('setFavicon', payload);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadFavicon',
                });
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async saveItem(key) {
      if (this.changed[key]) {
        this.loaders[key] = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Global settings successfully saved!',
        };
        try {
          const updates = [
            {
              key,
              value: this.currentSite[key],
            },
          ];
          if (key === 'selectionText') {
            updates.push(
              {
                key: 'mediaQueries',
                value: this.currentSite.mediaQueries,
              },
              {
                key: 'pageCSS',
                value: this.currentSite.pageCSS,
              },
            );
          }
          await this.$lightning([{
            name: 'updateSiteAttrs',
            payload: {
              siteId: this.currentSite._id,
              updates,
            },
          }]);
          this.$log.debug('successfully saved site changes');
          // since this is a site wide change, we need to regenerate the
          // site and push it to s3
          if (key !== 'colorPalette') {
            await this.callBuilder({
              publish: 'site',
              site: {
                _id: this.currentSite._id,
              },
            });
            this.$emit('set-initial', key);
          }
        } catch (err) {
          this.$log.error(err);
          notif.type = 'error';
          notif.message = err.message || err;
          await this._reportError({
            error: err,
            fn: 'save global settings',
          });
        } finally {
          this.loaders[key] = false;
          this.initial[key] = _.cloneDeep(this.currentSite[key]);
          this.changed[key] = false;
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    closeModal() {
      this.$emit('toggle-modal');
    },
    onCloseModal() {
      _.forOwn(this.changed, (changed, key) => {
        // if something was changed but not saved on close,
        // we need to revert it back
        if (changed) {
          this.cancelItem(key);
        }
      });
    },
    cancelItem(key) {
      if (this.changed[key]) {
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite,
          key,
          value: _.cloneDeep(this.initial[key]),
        });
        if (key === 'colorPalette') {
          this.setColorPickers();
        }
        if (key === 'selectionText') {
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key: 'mediaQueries',
            value: _.cloneDeep(this.initial.mediaQueries),
          });
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key: 'pageCSS',
            value: _.cloneDeep(this.initial.pageCSS),
          });
        }
        this.changed[key] = false;
        this.callCssGenerator();
      }
    },
    formatErrors(ers) {
      let string = '';
      _.forEach(ers, (er) => {
        string = string += ` ${er}`;
      });
      return string;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/global-options/theme/index.js